import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'store';
import { getAvatar } from 'utils/helpers';
import axios from 'axios';
import moment from 'moment';

import BaseTabs from 'components/Base/BaseTabs';
import TabGeneral from './TabGeneral';
import TabProvider from './TabProvider';
import TabPassword from './TabPassword';
import TabBackgroundCheck from './TabBackgroundCheck';
import TabAthena from './TabAthena';
import TabTeamContacts from './TabTeamContacts';



const TabConfigs = {
	provider: [
		{ value: 'General Information', Component: TabGeneral },
		{ value: 'Provider Profile', Component: TabProvider },
		{ value: 'Change Password', Component: TabPassword },
		{ value: 'Background Check', Component: TabBackgroundCheck },
	],
	customer: [
		{ value: 'General Information', Component: TabGeneral },
		{ value: 'Change Password', Component: TabPassword },
		{ value: 'Caregiver Contacts', Component: TabTeamContacts },
		{ value: 'Athena', Component: TabAthena, gate: 'athena' },
	],
	partner: [
		{ value: 'General Information', Component: TabGeneral },
		// { value: 'Partner Profile',  Component: TabPartner },
		{ value: 'Change Password', Component: TabPassword },
	],
};

const ProfileEdit = () => {
	const location = useLocation();
	const store = useStore();

	const { accessToPlatform } = store.auth;
	const { isCustomer, type, name, surname, avatar, companyName, isPartner } = store.auth.user;

	const [tab, setTab] = useState(null);
	const [canChangeAthena, setCanChangeAthena] = useState(false);

	useEffect(() => {
		if (!isCustomer || !accessToPlatform) {
			setCanChangeAthena(false);
			return;
		}

		let cancelled = false;

		axios.get('/athena/profile')
			.then(({ data }) => {
				if (cancelled) return;

				const isRegistered = !!data?.isRegistered;
				const hasCompletedEligibility = !!(
					data?.eligibility?.primaryInsurance?.isEligibilityChecked ||
					data?.eligibility?.secondaryInsurance?.isEligibilityChecked
				);
				const isEligible = !!(
					data?.eligibility?.primaryInsurance?.isEligible ||
					data?.eligibility?.secondaryInsurance?.isEligible
				);
				const hasConsent = !!data?.consent?.isConsentGiven;
				const consentOutdated = !!(
					data?.consent?.consentDate &&
					moment(data.consent.consentDate).isBefore(moment().subtract(1, 'year'))
				);

				setCanChangeAthena(
					isRegistered &&
					hasCompletedEligibility &&
					isEligible &&
					hasConsent &&
					!consentOutdated
				);
			})
			.catch((err) => {
				console.warn('Athena eligibility check failed', err);
				if (!cancelled) setCanChangeAthena(false);
			});

		return () => { cancelled = true; };
	}, [isCustomer, accessToPlatform]);

	const gates = { athena: canChangeAthena };
	const currentTabs = (TabConfigs[type] || []).filter(t => !t.gate || gates[t.gate]);


	useEffect(() => {
		if (!type || currentTabs.length === 0) return;

		const urlParams = new URLSearchParams(location.search);
		const tabParam = urlParams.get('tab');

		if (tabParam && currentTabs.some(t => t.value === tabParam)) {
			setTab(tabParam);
		} else {
			const fallback = !accessToPlatform
				? currentTabs[currentTabs.length - 1].value
				: currentTabs[0].value;
			setTab(fallback);
		}
	}, [type, accessToPlatform, location.search, canChangeAthena]);

	const ActiveTab = currentTabs.find(t => t.value === tab)?.Component;

	return (
		<div className="page profile-edit">
			<div className="profile-user">
				<div className="avatar">
					<img src={getAvatar(avatar)} alt="User" />
				</div>

				<div className="info">
					<div className="name">
						{name} {surname}
					</div>
					<div className="type">
						{type}{isPartner && companyName ? ` of ${companyName}` : ''}
					</div>
				</div>
			</div>

			<BaseTabs tabs={currentTabs} onTab={setTab} current={tab}>
				{ActiveTab && <ActiveTab />}
			</BaseTabs>
		</div>
	);
};

export default observer(ProfileEdit);